import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as ErrorIcon,
  Biotech as BiotechIcon,
  Settings as SettingsIcon,
  LinkOff as LinkOffIcon,
  Visibility,
  VisibilityOff,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import {
  idexxService,
  IdexxConfig,
  IdexxConfigPayload,
  IdexxRegion,
  IdexxRegionDescriptor,
} from '../../services/idexxService';

/**
 * Settings → Integrations → IDEXX panel. Mirrors KeystoneIntegrationSettings.
 *
 * Region is NOT a user setting (IDEXX 2026-07-21: VC+ credentials are linked
 * to a region). It appears only on FIRST connect — auto-shown read-only when
 * exactly one region is enabled, a one-time picker when several are — and is
 * locked on Reconfigure (the backend 409s a change; disconnect + reconnect to
 * switch). Options are data-driven by GET /idexx/regions (enabled rows only).
 */
const IdexxIntegrationSettings: React.FC = () => {
  const [config, setConfig] = useState<IdexxConfig | null>(null);
  const [regions, setRegions] = useState<IdexxRegionDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // INSTANTS in the CLINIC zone. ClinicIdexxConfig.lastConnectedAt / lastErrorAt /
  // refDataRefreshedAt are stamped `new Date()` server-side (idexx-config.service.ts,
  // idexx-poller.service.ts, idexx-refdata.service.ts) — DATE-KINDS §1 "Integrations".
  const formatInstant = useFormatInstant();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnauthorized(false);
    try {
      const [cfg, regs] = await Promise.all([
        idexxService.getConfig(),
        idexxService.listRegions(),
      ]);
      setConfig(cfg);
      setRegions(regs);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setUnauthorized(true);
      } else {
        setError(err.response?.data?.message || 'Failed to load IDEXX configuration');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (next: IdexxConfig) => {
    setConfig(next);
    setFormOpen(false);
    setSuccessMessage('IDEXX integration connected.');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const summary = await idexxService.refreshRefData(false);
      if (summary.skipped) {
        setSuccessMessage('Catalog already up to date.');
      } else {
        const { species, breeds, genders, tests } = summary.counts;
        setSuccessMessage(
          `Catalog refreshed: ${tests} tests, ${breeds} breeds, ${species} species, ${genders} genders.`,
        );
      }
      setTimeout(() => setSuccessMessage(null), 6000);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to refresh IDEXX catalog');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await idexxService.deleteConfig();
      setConfig({ configured: false });
      setDisconnectOpen(false);
      setSuccessMessage('IDEXX integration disconnected.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disconnect IDEXX integration');
    }
  };

  const isConfigured = config?.configured === true;
  const regionLabel = (region?: IdexxRegion) =>
    regions.find((r) => r.region === region)?.label ?? region ?? '—';

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <BiotechIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="h6">IDEXX VetConnect+</Typography>
              <Typography variant="body2" color="text.secondary">
                Equine lab requisitions and results (Reference Labs + IDEXX VetLab Station)
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {loading ? (
              <CircularProgress size={24} />
            ) : isConfigured ? (
              <Chip
                icon={<CheckIcon />}
                label="Connected"
                color="success"
                variant="outlined"
                size="small"
              />
            ) : (
              <Chip
                icon={<ErrorIcon />}
                label="Not Configured"
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {!loading && unauthorized && (
          <Typography variant="body2" color="text.secondary">
            Only clinic owners and admins can configure the IDEXX integration.
          </Typography>
        )}

        {!loading && !unauthorized && !isConfigured && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your VetConnect+ credentials to order bloodwork from inside
              StableTrack and receive results back on the horse's chart.
            </Typography>
            {regions.length === 0 ? (
              <Alert severity="info">
                No IDEXX regions are enabled yet. Contact StableTrack support.
              </Alert>
            ) : (
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => setFormOpen(true)}
              >
                Configure
              </Button>
            )}
          </Box>
        )}

        {!loading && !unauthorized && isConfigured && config?.configured && (
          <Box>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Region
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {regionLabel(config.region)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {config.apiUsername}
                </Typography>
              </Box>
              {config.limsId && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    LIMS ID
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {config.limsId}
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Last Connected
                </Typography>
                <Typography variant="body2" fontWeight={500} data-testid="idexx-last-connected">
                  {formatInstant(config.lastConnectedAt, INSTANT_FORMATS.dateTimeLong) || '—'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Catalog Last Refreshed
                </Typography>
                <Typography variant="body2" fontWeight={500} data-testid="idexx-catalog-refreshed">
                  {formatInstant(config.refDataRefreshedAt, INSTANT_FORMATS.dateTimeLong) ||
                    'Not yet — click Refresh Catalog'}
                </Typography>
              </Box>
              {config.lastError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Last error: {config.lastError}
                  {config.lastErrorAt && (
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      data-testid="idexx-last-error-at"
                    >
                      {formatInstant(config.lastErrorAt, INSTANT_FORMATS.dateTimeLong)}
                    </Typography>
                  )}
                </Alert>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setFormOpen(true)}
              >
                Reconfigure
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh Catalog
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setDisconnectOpen(true)}
              >
                Disconnect
              </Button>
            </Stack>
          </Box>
        )}
      </CardContent>

      <IdexxConfigDialog
        open={formOpen}
        regions={regions}
        initial={config?.configured ? config : undefined}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog open={disconnectOpen} onClose={() => setDisconnectOpen(false)}>
        <DialogTitle>Disconnect IDEXX Integration?</DialogTitle>
        <DialogContent>
          <Typography>
            New lab requisitions won't be sent to IDEXX until you reconnect. Past
            lab results and orders remain viewable on horse charts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

interface IdexxConfigDialogProps {
  open: boolean;
  regions: IdexxRegionDescriptor[];
  initial?: { region: IdexxRegion; apiUsername: string; limsId?: string | null };
  onClose: () => void;
  onSaved: (next: IdexxConfig) => void;
}

const IdexxConfigDialog: React.FC<IdexxConfigDialogProps> = ({
  open,
  regions,
  initial,
  onClose,
  onSaved,
}) => {
  const [region, setRegion] = useState<IdexxRegion | ''>('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [limsId, setLimsId] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isReconfigure = Boolean(initial);

  // Reset form whenever the dialog opens with fresh props.
  useEffect(() => {
    if (open) {
      // Reconfigure: locked to the stored region. First connect: auto-select
      // only when a single region is enabled — with several, force an explicit
      // choice (VC+ credentials are linked to one region; no silent default).
      const defaultRegion =
        initial?.region ?? (regions.length === 1 ? regions[0].region : '');
      setRegion(defaultRegion);
      setUsername(initial?.apiUsername ?? '');
      setLimsId(initial?.limsId ?? '');
      setPassword('');
      setShowPassword(false);
      setTestResult(null);
      setSaveError(null);
    }
  }, [open, initial, regions]);

  const canSubmit =
    region !== '' && username.trim().length > 0 && password.length > 0;

  const buildPayload = (): IdexxConfigPayload => ({
    // Omit region on reconfigure — the backend resolves it from the stored
    // config (and 409s an attempted change).
    region: isReconfigure ? undefined : (region as IdexxRegion),
    apiUsername: username.trim(),
    apiPassword: password,
    limsId: limsId.trim() || undefined,
  });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setSaveError(null);
    try {
      const result = await idexxService.testConfig(buildPayload());
      setTestResult({ ok: result.success, message: result.message });
    } catch (err: any) {
      setTestResult({
        ok: false,
        message: err.response?.data?.message || 'Test failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = await idexxService.saveConfig(buildPayload());
      onSaved(next);
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Reconfigure IDEXX' : 'Configure IDEXX'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use the VetConnect+ credentials IDEXX provided for your practice.
          Test the connection before saving — credentials are only stored after a
          successful test.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {isReconfigure ? (
            <TextField
              label="IDEXX account region"
              value={
                regions.find((r) => r.region === initial?.region)?.label ??
                initial?.region ??
                ''
              }
              disabled
              fullWidth
              helperText="Region is linked to your VetConnect+ account and can't be changed. Disconnect and reconnect to switch regions."
            />
          ) : regions.length === 1 ? (
            <TextField
              label="IDEXX account region"
              value={`${regions[0].label} (${regions[0].currencyCode})`}
              disabled
              fullWidth
              helperText="Determined by your VetConnect+ account."
            />
          ) : (
            <TextField
              select
              label="IDEXX account region"
              value={region}
              onChange={(e) => setRegion(e.target.value as IdexxRegion)}
              fullWidth
              helperText="Your VetConnect+ credentials are linked to one region — pick the region of your IDEXX account. It can't be changed after connecting. With the wrong region, IDEXX may reject orders (note length, required client fields)."
            >
              {regions.map((r) => (
                <MenuItem key={r.region} value={r.region}>
                  {r.label} ({r.currencyCode})
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="VetConnect+ API username"
            fullWidth
            autoComplete="off"
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={
              initial
                ? 'Re-enter password to update credentials'
                : 'VetConnect+ API password'
            }
            fullWidth
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="LIMS ID (optional)"
            value={limsId}
            onChange={(e) => setLimsId(e.target.value)}
            placeholder="Required only when your practice has multiple lab accounts"
            fullWidth
            autoComplete="off"
            helperText="Only needed when your practice has more than one IDEXX lab account. Find it in VetConnect+ → Settings (gear icon) → Practice Preferences."
          />

          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'error'}>
              {testResult.message}
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleTest}
          disabled={!canSubmit || testing || saving}
          variant="outlined"
        >
          {testing ? <CircularProgress size={20} /> : 'Test Connection'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSubmit || saving || testing}
          variant="contained"
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdexxIntegrationSettings;
